import { useState } from "react";
import { toast } from "../../utils/toast";
import { ROLES } from "../../auth/roles";
import { errorMessageOf, fieldErrorsOf, problemOf, scholarshipsApi } from "../../api/scholarships";
import { Button, Input, Modal, SearchableSelect, Select } from "../ui";
import { useInstitutions } from "./institutions";

// The roles the HEC Admin may give; most students register themselves, so Student comes last
const ROLE_OPTIONS = [
  { value: ROLES.ADMIN, label: "Scholarship Admin (HEC)" },
  { value: ROLES.DG, label: "Director General" },
  { value: ROLES.MINISTER, label: "Minister" },
  { value: ROLES.BRD, label: "BRD" },
  { value: ROLES.EVALUATOR, label: "HEC Evaluator" },
  { value: ROLES.SCHOLARSHIP_OWNER, label: "Scholarship Owner (partner)" },
  { value: ROLES.HLI_ADMIN, label: "HLI Admin (public institution's loan officer)" },
  { value: ROLES.APPLICANT, label: "Student" },
];

const EMPTY = { email: "", firstName: "", lastName: "", role: "", institutionId: "", nationalId: "", phoneNumber: "" };

/**
 * Creates an account: the person gets the role and an email to verify their address and set a
 * password. An HLI admin is linked to their institution at once; a student needs their national ID
 * and phone number, as when they register themselves.
 */
export default function CreateAccountModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const hli = form.role === ROLES.HLI_ADMIN;
  const student = form.role === ROLES.APPLICANT;
  const { data: institutions = [], isPending } = useInstitutions({ enabled: open && hli });

  const close = () => {
    setForm(EMPTY);
    setErrors({});
    onClose();
  };
  const field = (name) => ({
    value: form[name],
    onChange: (e) => {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
      setForm((prev) => ({ ...prev, [name]: e.target.value }));
    },
    error: errors[name],
    disabled: busy,
  });

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      const { data } = await scholarshipsApi.post("admin/accounts", {
        email: form.email.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        role: form.role,
        ...(hli && form.institutionId && { institutionId: form.institutionId }),
        ...(student && { nationalId: form.nationalId.trim(), phoneNumber: form.phoneNumber.trim() }),
      });
      if (data.setUpEmailSent) {
        toast.success(`Account created for ${data.firstName} ${data.lastName}. They get an email to set their password.`, { autoClose: 5000 });
      } else {
        // The account stands; Keycloak's mail server failed, so the set-up link must be sent again from Keycloak
        toast.warning(`Account created for ${data.firstName} ${data.lastName}, but the set-up email could not be sent. Send it again from Keycloak.`, { autoClose: 8000 });
      }
      setForm(EMPTY);
      onCreated(data);
    } catch (error) {
      console.error(`Failed to create a ${form.role} account:`, error);
      setErrors(fieldErrorsOf(problemOf(error)));
      toast.error(errorMessageOf(error, "Failed to create the account. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      dismissible={!busy}
      size="md"
      title="Create an account"
      description="Students usually create their own account when they register; create one here only for a student who cannot."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" type="submit" form="create-account" loading={busy}>
            Create account
          </Button>
        </>
      }
    >
      <form id="create-account" onSubmit={submit} className="grid gap-4 md:grid-cols-2">
        <Input label="First name" required maxLength={100} {...field("firstName")} />
        <Input label="Last name" required maxLength={100} {...field("lastName")} />
        <Input type="email" label="Email" required maxLength={254} className="md:col-span-2" {...field("email")} />
        <Select label="Role" required placeholder="Choose a role" options={ROLE_OPTIONS} className="md:col-span-2" {...field("role")} />
        {student && (
          <>
            <Input
              label="National ID"
              required
              inputMode="numeric"
              placeholder="16 digits"
              pattern="\s*(\d\s*){16}"
              title="The 16-digit national ID number"
              maxLength={20}
              {...field("nationalId")}
            />
            <Input
              type="tel"
              label="Phone number"
              required
              placeholder="07XXXXXXXX"
              pattern="\s*(\+?250|0)?\s*7[\d\s\-]{8,12}"
              title="A Rwandan mobile number, like 0781234567 or +250781234567"
              maxLength={20}
              {...field("phoneNumber")}
            />
          </>
        )}
        {hli && (
          <SearchableSelect
            label="Institution"
            required
            className="md:col-span-2"
            placeholder={isPending ? "Loading institutions…" : "Select an institution"}
            options={institutions.map((i) => ({ value: String(i.id), label: i.institutionCode ? `${i.fullName} (${i.institutionCode})` : i.fullName }))}
            {...field("institutionId")}
            disabled={busy || isPending}
          />
        )}
      </form>
    </Modal>
  );
}
